import type { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';

import {
  needsYouBannerBody,
  needsYouBannerTitle,
  READY_BANNER_BODY,
  readyBannerTitle,
} from '../copy';
import type { PayerAudience } from '../types';

/**
 * The two banners above the payer table. Both are derived from folded state:
 * the amber one counts `needs_customer` payers, the green one names the
 * payers confirmed ready for a first claim. Neither invents a number.
 */
export function NeedsYouBanner({
  count,
  audience,
}: {
  count: number;
  audience?: PayerAudience;
}) {
  return (
    <Box
      sx={{
        backgroundColor: '#fff8ed',
        border: '1px solid #f0cfa0',
        borderLeft: '4px solid #d97706',
        borderRadius: '8px',
        px: '18px',
        py: '15px',
      }}
    >
      <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#7c3f06' }}>
        {needsYouBannerTitle(count, audience)}
      </Typography>
      <Typography sx={{ fontSize: '13px', lineHeight: 1.55, color: '#8a5a1a', mt: '3px' }}>
        {needsYouBannerBody(audience)}
      </Typography>
    </Box>
  );
}

export function ReadyBanner({
  payerNames,
  audience,
  action,
}: {
  payerNames: string[];
  audience?: PayerAudience;
  /** The clinic's "Submit first claim" link; staff get no clinic-scoped destination. */
  action?: ReactNode;
}) {
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        backgroundColor: '#eefaf1',
        border: '1px solid #bfe4cb',
        borderRadius: '8px',
        px: '18px',
        py: '15px',
      }}
    >
      <Box sx={{ minWidth: 0, flex: '1 1 340px' }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#14532d' }}>
          {readyBannerTitle(payerNames, audience)}
        </Typography>
        <Typography sx={{ fontSize: '13px', lineHeight: 1.55, color: '#3f6b4d', mt: '3px' }}>
          {READY_BANNER_BODY}
        </Typography>
      </Box>
      {action}
    </Stack>
  );
}
